import { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, Check, Search } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import { avatarColors } from '../../../helpers/Project-Report/utils/helpers';

const PicDropdown = ({ taskId, value, onChange, accent, isReadOnly, cellInputStyle, picOptions, picSearch, setPicSearch, onPicSearch, onRefreshPic, onOpen, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Default style if none provided
  const defaultStyle = {
    border: '1.5px solid #e2e8f0', borderRadius: '8px',
    padding: '6px 10px', fontSize: '12px', color: '#0f172a',
    background: '#fafafa', cursor: 'pointer', outline: 'none',
    width: '100%', transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif",
  };

  const btnStyle = cellInputStyle || defaultStyle;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (isReadOnly) return;
    if (isOpen) {
      setIsOpen(false);
      onClose?.();
    } else {
      setIsOpen(true);
      setPicSearch('');
      onRefreshPic();
      onOpen?.();
    }
  };

  const handleSelect = (picValue) => {
    onChange(picValue);
    setIsOpen(false);
    onClose?.();
  };

  const displayName = value ? String(value).split('-')[1] || value : null;

  const filteredOptions = picOptions.filter(
    p => !picSearch || (p.nama && p.nama.toLowerCase().includes(picSearch.toLowerCase()))
  );

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <Tooltip
        target={`.pic-tooltip-${taskId}`}
        position="top"
        mouseTrack
        mouseTrackTop={15}
        pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } } }}
      />
      <button
        type="button"
        className={`pic-tooltip-${taskId}`}
        data-pr-tooltip={displayName || 'Klik untuk mengubah PIC'}
        onClick={handleOpen}
        style={{
          ...btnStyle,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderColor: isOpen ? accent : '#e2e8f0',
          boxShadow: isOpen ? `0 0 0 3px ${accent}20` : 'none',
          color: value ? '#0f172a' : '#94a3b8'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
          <User size={13} color={value ? accent : '#94a3b8'} style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '11.5px', fontWeight: value ? '600' : '400' }}>
            {displayName || '— Pilih PIC'}
          </span>
        </div>
        <ChevronDown size={12} color="#94a3b8" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            zIndex: 350, background: '#fff', borderRadius: '12px',
            border: '1.5px solid #e2e8f0', boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden', animation: 'dropFadeIn 0.15s ease',
            display: 'flex', flexDirection: 'column',
            maxHeight: '260px', width: 'fit-content', maxWidth: '260px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={12} color="#94a3b8" style={{ position: 'absolute', left: '10px' }} />
              <input
                autoFocus
                type="text"
                placeholder="Cari PIC..."
                value={picSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setPicSearch(val);
                  onPicSearch(val);
                }}
                style={{
                  width: '100%', padding: '6px 10px 6px 28px',
                  border: '1px solid #e2e8f0', borderRadius: '6px',
                  fontSize: '11.5px', outline: 'none', color: '#0f172a',
                  fontFamily: "'Inter', sans-serif"
                }}
                onFocus={(e) => { e.target.style.borderColor = accent; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px' }}>
            <Tooltip
              target=".pic-option-tooltip"
              position="right"
              mouseTrack
              mouseTrackTop={15}
              pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } } }}
            />
            {filteredOptions.map((p, i) => {
              const picVal = p.NIK ? `${p.NIK}-${p.nama}-${p.bagian}` : `${p.nama}-${p.bagian}`;
              const isSelected = value === p.nama || value === picVal;
              return (
                <div
                  key={i}
                  className="pic-option-tooltip"
                  data-pr-tooltip={p.nama || 'Kosongkan'}
                  onClick={() => handleSelect(picVal)}
                  style={{
                    padding: '8px 10px', fontSize: '12px', color: '#334155',
                    cursor: 'pointer', borderRadius: '6px',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: isSelected ? `${accent}15` : 'transparent',
                    fontWeight: isSelected ? '600' : '400',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: p.nama ? avatarColors[p.id % avatarColors.length] : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '9px', fontWeight: 'bold', flexShrink: 0 }}>
                    {p.avatar ? p.avatar : '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', fontWeight: isSelected ? '700' : '600' }}>
                      {p.nama || '— Kosongkan —'}
                    </span>
                    {p.nama && (
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {p.NIK} - {p.department}
                      </span>
                    )}
                  </div>
                  {isSelected && <Check size={12} color={accent} style={{ flexShrink: 0 }} />}
                </div>
              );
            })}
            {filteredOptions.length === 0 && (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '11.5px', color: '#94a3b8' }}>
                Tidak ditemukan
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PicDropdown;
